import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '短剧档案库', to: '/dramas' },
  { label: '解读方法', to: '/#method' },
  { label: '常见问题', to: '/#faq' },
  { label: '关于我们', to: '/about' },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const solid = !overlay || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          aria-label={`${siteInfo.brand} 首页`}
        >
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 shrink-0">
            <i className="ri-file-shield-2-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide transition-colors ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              {siteInfo.brand}
            </span>
            <span
              className={`font-label text-[10px] tracking-[0.2em] transition-colors ${
                solid ? 'text-foreground-500' : 'text-background-200'
              }`}
            >
              {siteInfo.brandEn}
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navLinks.map((link) => {
            const active =
              link.to === location.pathname ||
              (link.to.includes('#') && link.to.split('#')[0] === location.pathname);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3.5 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  solid
                    ? active
                      ? 'text-primary-600 bg-primary-50 font-medium'
                      : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                    : active
                      ? 'text-background-50 bg-background-50/15 font-medium'
                      : 'text-background-100/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/dramas"
            className="ml-3 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            查一部剧
          </Link>
        </nav>

        <button
          type="button"
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
            solid ? 'text-foreground-900' : 'text-background-50'
          }`}
          aria-label={open ? '关闭菜单' : '打开菜单'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <i className={open ? 'ri-close-line text-2xl' : 'ri-menu-3-line text-2xl'}></i>
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 pb-4 pt-2">
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-3 text-sm text-foreground-800 border-b border-background-100 last:border-none hover:text-primary-600 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/dramas"
            className="mt-3 w-full flex items-center justify-center px-4 py-3 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            查一部剧
          </Link>
        </div>
      )}
    </header>
  );
}